import { Lightbulb } from 'lucide-react';

export function MeasurementGuide() {
  return (
    <div>
      <h3 className="section-title">How to Measure Packages</h3>
      <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
        <GuideStep
          step={1}
          title="Measure Length (L)"
          description="The longest side of the package. Measure from end to end along the longest edge."
          tip="Always measure the outer dimensions including any bulging or irregular shapes."
        />
        <GuideStep
          step={2}
          title="Measure Width (W)"
          description="The shorter side perpendicular to length. Measure across the package."
          tip="Rotate the package 90° from the length measurement."
        />
        <GuideStep
          step={3}
          title="Measure Height (H)"
          description="The vertical dimension from bottom to top when the package is sitting flat."
          tip="Include handles, lids, or any protrusions that affect stacking."
        />
        <GuideStep
          step={4}
          title="Weigh the Package"
          description="Use a calibrated scale. Record the gross weight including all packaging materials."
          tip="Weigh each package type separately, even if dimensions are identical."
        />
        <GuideStep
          step={5}
          title="Count Quantity"
          description="Enter the number of identical packages with these exact dimensions and weight."
          tip="If packages vary in size or weight, enter them as separate line items."
        />

        <div
          style={{
            padding: 'var(--space-3)',
            background: 'var(--color-bg-secondary)',
            borderRadius: 'var(--radius-md)',
            borderLeft: '3px solid var(--color-accent)',
          }}
        >
          <strong>Common Mistakes</strong>
          <ul style={{ margin: 'var(--space-2) 0 0', paddingLeft: 'var(--space-4)', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
            <li>Measuring inner box dimensions instead of outer</li>
            <li>Forgetting to include pallet weight in gross weight</li>
            <li>Using the wrong unit (cm vs mm vs inches)</li>
            <li>Not accounting for non-rectangular shapes — use the bounding box</li>
            <li>Rounding down instead of rounding up for carrier rules</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

function GuideStep({ step, title, description, tip }: { step: number; title: string; description: string; tip: string }) {
  return (
    <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
      <div
        style={{
          width: 32,
          height: 32,
          borderRadius: '50%',
          background: 'var(--color-accent)',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 'var(--font-weight-bold)',
          fontSize: 'var(--font-size-sm)',
          flexShrink: 0,
        }}
      >
        {step}
      </div>
      <div>
        <div style={{ fontWeight: 'var(--font-weight-semibold)', marginBottom: 'var(--space-1)' }}>{title}</div>
        <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', margin: 0 }}>{description}</p>
        <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', margin: 'var(--space-1) 0 0', fontStyle: 'italic' }}>
          <Lightbulb size={12} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 'var(--space-1)' }} /> {tip}
        </p>
      </div>
    </div>
  );
}
